import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, MailCheck, CheckCircle2 } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { AuthLayout, AuthField, AuthSubmit, AuthError } from "../components/app/AuthLayout";

const notLive = (e) => e?.response?.status === 404 || e?.response?.status === 405;
const UNAVAILABLE = "Password reset isn't switched on yet. Please contact GAMEDAY support to reset your password.";

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/forgot-password", { email: email.trim(), reset_url: `${window.location.origin}/reset-password` });
      setSent(true);
    } catch (err) {
      setError(notLive(err) ? UNAVAILABLE : errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout tagline={<>Back in<br /><span className="text-[#39FF14]">the game.</span></>}>
      {sent ? (
        <div className="space-y-5" data-testid="forgot-sent">
          <MailCheck className="h-10 w-10 text-[#39FF14]" />
          <h1 className="font-display text-5xl font-black uppercase leading-none text-white">Check your inbox.</h1>
          <p className="text-slate-400">If an account exists for <span className="text-white">{email}</span>, we've emailed a link to reset your password. It expires in 1 hour.</p>
          <Link to="/login" data-testid="forgot-back-login" className="inline-block font-semibold text-[#39FF14] hover:underline">Back to log in</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-6" data-testid="forgot-form">
          <div>
            <p className="font-mono-gd text-xs uppercase tracking-[0.25em] text-[#39FF14]">Reset password</p>
            <h1 className="mt-3 font-display text-5xl font-black uppercase leading-none text-white">Forgot it?</h1>
            <p className="mt-3 text-slate-400">Enter your account email and we'll send you a reset link. It works for the mobile app too.</p>
          </div>
          <AuthField label="Email" type="email" required data-testid="forgot-email-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@school.edu" autoComplete="email" />
          <AuthError error={error} testid="forgot-error" />
          <AuthSubmit busy={busy} busyLabel="Sending…" testid="forgot-submit-button">Send reset link <ArrowRight className="h-4 w-4" /></AuthSubmit>
          <Link to="/login" data-testid="forgot-login-link" className="inline-block text-sm text-slate-400 hover:text-white">Back to log in</Link>
        </form>
      )}
    </AuthLayout>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (pw !== pw2) { setError("Passwords don't match."); return; }
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/reset-password", { token, password: pw });
      setDone(true);
    } catch (err) {
      setError(notLive(err) ? UNAVAILABLE : errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout tagline={<>New password.<br /><span className="text-[#39FF14]">Same game.</span></>}>
      {!token ? (
        <div className="space-y-4" data-testid="reset-invalid">
          <h1 className="font-display text-5xl font-black uppercase leading-none text-white">Link missing.</h1>
          <p className="text-slate-400">This reset link is incomplete. Request a new one.</p>
          <Link to="/forgot-password" className="font-semibold text-[#39FF14] hover:underline">Request a new link</Link>
        </div>
      ) : done ? (
        <div className="space-y-5" data-testid="reset-done">
          <CheckCircle2 className="h-10 w-10 text-[#39FF14]" />
          <h1 className="font-display text-5xl font-black uppercase leading-none text-white">Password updated.</h1>
          <p className="text-slate-400">Use your new password here and in the GAMEDAY mobile app.</p>
          <Link to="/login" data-testid="reset-login-link" className="inline-flex items-center gap-2 rounded-full bg-[#39FF14] px-6 py-3 font-semibold text-[#0A0E1A]">Log in <ArrowRight className="h-4 w-4" /></Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-6" data-testid="reset-form">
          <div>
            <p className="font-mono-gd text-xs uppercase tracking-[0.25em] text-[#39FF14]">Reset password</p>
            <h1 className="mt-3 font-display text-5xl font-black uppercase leading-none text-white">Choose a new one.</h1>
          </div>
          <AuthField label="New password" type="password" required minLength={8} data-testid="reset-password-input" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="At least 8 characters" autoComplete="new-password" />
          <AuthField label="Confirm password" type="password" required data-testid="reset-confirm-input" value={pw2} onChange={(e) => setPw2(e.target.value)} placeholder="Type it again" autoComplete="new-password" />
          <AuthError error={error} testid="reset-error" />
          <AuthSubmit busy={busy} busyLabel="Saving…" testid="reset-submit-button">Save new password <ArrowRight className="h-4 w-4" /></AuthSubmit>
        </form>
      )}
    </AuthLayout>
  );
}
